import { useTranslation } from 'next-i18next/pages';

import { NAV_MENU_ITEMS } from '@/data/shared/navMenu';

import { NavMenuAnchor } from './NavMenuAnchor';

/**
 * ### FooterNavColor
 *
 * Footer colour scheme the site map follows; mirrors the `color` prop of both footers.
 */
export type FooterNavColor = 'white' | 'black';

/**
 * ### FooterNavVariant
 *
 * Which footer hosts the site map; decides columns and spacing.
 */
export type FooterNavVariant = 'desktop' | 'mobile';

/**
 * ### FooterNavProps
 *
 * Appearance of the footer site map.
 */
export interface FooterNavProps {
  /**
   * ### color
   *
   * Footer colour scheme to blend with.
   */
  color: FooterNavColor;

  /**
   * ### variant
   *
   * Desktop lays the groups out in a row; mobile folds them into two columns.
   */
  variant: FooterNavVariant;
}

/**
 * Colour comes from the footer, so every tone is spelled out here as complete
 * class names for Tailwind to pick up.
 */
const TONE: Record<FooterNavColor, { heading: string; link: string }> = {
  white: {
    heading: 'text-apps-coreGrayBlack',
    link: 'text-apps-coreGray02 hover:text-apps-coreGrayBlack focus-visible:text-apps-coreGrayBlack',
  },
  black: {
    heading: 'text-apps-brandGray950',
    link: 'text-apps-coreGray03 hover:text-apps-coreGrayWhite focus-visible:text-apps-coreGrayWhite',
  },
};

const LAYOUT: Record<FooterNavVariant, { nav: string; group: string }> = {
  desktop: {
    nav: 'grid grid-cols-2 gap-x-12 gap-y-10 sm:grid-cols-3 xl:grid-cols-6',
    group: 'flex min-w-0 flex-col gap-4',
  },
  mobile: {
    nav: 'grid grid-cols-2 gap-x-6 gap-y-8',
    group: 'flex flex-col gap-3',
  },
};

const HEADING = 'text-sm font-bold leading-[140%] tracking-[-0.28px]';
const LINK =
  'text-sm font-medium leading-[150%] tracking-[-0.28px] transition-colors duration-200';

/**
 * ### FooterNav
 *
 * The footer site map mirrors the global navigation's primary and secondary
 * entries. External resources
 * open in a new window with the shared external-link mark.
 *
 * @param props Colour scheme and host footer
 * @returns The labelled `<nav>` of grouped links
 * @see {@link NAV_MENU_ITEMS} The visible navigation structure rendered
 */
export function FooterNav({ color, variant }: FooterNavProps) {
  const { t } = useTranslation('common');
  const tone = TONE[color];
  const layout = LAYOUT[variant];
  const menuItems = NAV_MENU_ITEMS;

  return (
    <nav className={layout.nav} aria-label={t('사이트맵')}>
      {menuItems.map((menu, index) => {
        const submenu = menu.depth2;
        const headingId = `footer-nav-${variant}-${index}`;

        return (
          <div key={menu.depth1.url} className={layout.group}>
            {submenu ? (
              <>
                <h2 id={headingId} className={`${HEADING} ${tone.heading}`}>
                  {t(menu.depth1.label)}
                </h2>
                <ul className="flex flex-col gap-2" aria-labelledby={headingId}>
                  {submenu.map((link) => (
                    <li key={link.url}>
                      <NavMenuAnchor
                        link={link}
                        className={`${LINK} ${tone.link}`}
                      />
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <NavMenuAnchor
                link={menu.depth1}
                className={`${HEADING} ${tone.link}`}
              />
            )}
          </div>
        );
      })}
    </nav>
  );
}
